import React, { useEffect, useState } from "react"
import { useTranslation } from 'react-i18next'
import { useSelector } from "react-redux"
import { Modal } from 'react-bootstrap'
import DatePicker from "react-datepicker"
import Select from 'react-select'

import { httpOk, GET, POST } from 'helpers/api'
import { formatDate, returnMinDate, notify } from 'helpers/helpers'

/**
 * Перемещение прихода в другой филиал. Состав документа уходит как есть — здесь
 * выбирают только куда, от кого и какой датой. Остальное считает бэк:
 * /wms-transfer-branch-from-document-in.
 */
function TransferBranchModal({ show, setShow, documentIn, onSuccess }) {
	const { t } = useTranslation()
	const reduxAccount = useSelector(state => state.account)

	const [poses, setPoses] = useState([])
	const [suppliers, setSuppliers] = useState([])
	const [data, setData] = useState({})

	async function getData() {
		var response = await GET('/helper/poses-chain-transfer', {}, {})
		if (httpOk(response)) {
			// Точка-источник сама себе филиалом-получателем быть не может
			setPoses(response.data.filter(item => item.id !== reduxAccount.pos_id))
		}

		response = await GET('/helper/internal-suppliers', {}, {})
		if (httpOk(response)) setSuppliers(response.data)
	}

	async function createTransfer() {
		const response = await POST('/wms-transfer-branch-from-document-in', {
			'document_in_id': documentIn.id,
			'to_pos_id': data.to_pos_id,
			'supplier_id': data.supplier_id,
			'created_at': data.created_at,
			'note': data.note,
		}, { loader: true })

		if (httpOk(response)) {
			notify(t('transfer_created'), 'success')
			setShow(false)
			if (onSuccess) onSuccess()
		}
	}

	useEffect(() => {
		if (!show) return

		setData({
			'to_pos_id': null,
			'supplier_id': null,
			'created_at': formatDate(new Date(), 'yyyy-MM-dd'),
			'note': `${t('transfer_document_in_note')} №${documentIn.id} — ${formatDate(documentIn.created_at, 'dd.MM.yy')}`,
		})
		getData()
	}, [show]) // eslint-disable-line react-hooks/exhaustive-deps

	return (
		<Modal show={show} animation={false} centered scrollable
			onHide={() => setShow(false)}>
			<Modal.Header closeButton>
				<Modal.Title>{t('transfer_to_branch')}</Modal.Title>
			</Modal.Header>
			<Modal.Body>
				<div className="form-group">
					<label>{t('date')}<span className="required-mark">*</span></label>
					<div className="calendar-input">
						<DatePicker
							showMonthDropdown
							showYearDropdown
							dropdownMode="select"
							yearDropdownItemNumber={2}
							className="form-control"
							dateFormat="dd.MM.yyyy"
							selected={data.created_at ? new Date(data.created_at) : ''}
							onChange={(date) => setData({ ...data, 'created_at': formatDate(date, 'yyyy-MM-dd') })}
							minDate={returnMinDate(new Date(), 20)}
							maxDate={new Date()} />
						<i className="uil uil-calendar-alt"></i>
					</div>
				</div>

				<div className="form-group">
					<label>{t('branch')}<span className="required-mark">*</span></label>
					<Select
						options={poses}
						value={poses.find(option => option.id === data.to_pos_id) || ''}
						onChange={(option) => setData({ ...data, 'to_pos_id': option.id })}
						placeholder=""
						noOptionsMessage={() => t('list_empty')}
						menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
						styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
						getOptionLabel={(option) => option.name}
						getOptionValue={(option) => option.id}
					/>
				</div>

				<div className="form-group">
					<label>{t('supplier')}<span className="required-mark">*</span></label>
					<Select
						options={suppliers}
						value={suppliers.find(option => option.id === data.supplier_id) || ''}
						onChange={(option) => setData({ ...data, 'supplier_id': option.id })}
						placeholder=""
						noOptionsMessage={() => t('list_empty')}
						menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
						styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
						getOptionLabel={(option) => option.name}
						getOptionValue={(option) => option.id}
					/>
				</div>

				<div className="form-group">
					<label>{t('note')}</label>
					<input type="text" className="form-control"
						value={data.note ?? ''}
						onChange={(e) => setData({ ...data, 'note': e.target.value })} />
				</div>

				<div className="text-muted">
					{t('transfer_document_in_hint')}
				</div>
			</Modal.Body>
			<Modal.Footer>
				<div className="d-flex gap-2 w-100">
					<button className="btn btn-warning w-100" onClick={() => setShow(false)}>
						{t('cancel')}
					</button>
					<button className="btn btn-primary w-100"
						disabled={!(data.to_pos_id && data.supplier_id && data.created_at)}
						onClick={() => createTransfer()}>
						{t('transfer_to_branch')}
					</button>
				</div>
			</Modal.Footer>
		</Modal>
	)
}

export default TransferBranchModal
